// @mui
import { Grid, Typography, Box, useTheme } from "@mui/material";
import { AgeDistributionGraph } from "./AgeDistributionGraph";
import GenderDistributionGraph from "./GenderDistributionGraph";
import { generateGenderGraphData } from "../utils";
import { BrandResult } from "../SearchBar";
import { SkeletonCard } from "~src/components/SkeletonCard";
import usePermissions from "~src/hooks/usePermissions";
import { AgeDistribution } from "../CompetitorAnalytics";

// ---------------------------------------------------------------------

export type AnalyticsData = {
  [date: string]: {
    Direct: string;
    Referrals: string;
    Search: string;
    Social: string;
    Mail: string;
    Display: string;
  };
};

export type LatestTrafficData = {
  Direct: string;
  Referrals: string;
  Search: string;
  Social: string;
  Mail: string;
  Display: string;
};

export type GenderDistributionType = {
  Female: string;
  Male: string;
};

export type AgeDistributionType = {
  "18 - 24": string;
  "25 - 34": string;
  "35 - 44": string;
  "45 - 54": string;
  "55 - 64": string;
  "65+"?: string;
};

export type DistributionType = {
  GenderDistribution: GenderDistributionType;
  AgeDistribution: AgeDistributionType;
};

export type DemographicsDataType = {
  [timestamp: string]: DistributionType;
};

export default function Demographics({
  loading,
  ageDistributions,
  genderDistributions,
  selectedBrands,
}: {
  loading: boolean;
  ageDistributions: AgeDistribution[];
  genderDistributions: GenderDistributionType[];
  selectedBrands: BrandResult[];
}) {
  const theme = useTheme();
  const { premiumCommunity } = usePermissions();
  const hasNonZeroValues = (values: number[]) =>
    values.every((value) => value > 0);

  // Function to check if all values in an object are "--"
  const allValuesAreDashes = (data: AgeDistribution): boolean => {
    return Object.values(data).every((value) => value === "--");
  };

  // Function to check if all objects in the array have all values as "--"
  const allObjectsInvalid = (dataArray: AgeDistribution[]): boolean => {
    return dataArray.every((data) => allValuesAreDashes(data));
  };

  if (
    allObjectsInvalid(ageDistributions) &&
    !hasNonZeroValues(generateGenderGraphData(genderDistributions).values)
  ) {
    return null;
  }
  return (
    <Box
      sx={{
        backgroundColor: theme.palette.common.white,
        border: 1,
        borderColor: theme.palette.grey[200],
        borderRadius: "12px",
        mt: 1,
        p: 4,
      }}
    >
      <Typography variant="h5" sx={{ mb: 1 }}>
        Demographics
      </Typography>
      <Grid
        container
        id="traffic-revenue"
        sx={{
          columnGap: 2,
          width: "100%"
        }}>
        {hasNonZeroValues(
          generateGenderGraphData(genderDistributions).values,
        ) && (
          <Grid 
            sx={{
              height: "460px",
              backgroundColor: "white",
              p: 4,
              display: "grid",
              gridTemplateRows: "auto 1fr",
              border: 1,
              borderRadius: 2,
              borderColor: "grey.200",
              mb: 2,
            }}
            size={{ xs: 12, md: 4 }}
            
          >
            <Typography variant="h5">Gender Distribution</Typography>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <GenderDistributionGraph
                graphData={generateGenderGraphData(genderDistributions)}
              />
            </div>
          </Grid>
        )}
        {!allObjectsInvalid(ageDistributions) && (
          <AgeDistributionGraph
            graphData={
              !premiumCommunity && ageDistributions.length > 3
                ? ageDistributions.slice(0, 3)
                : ageDistributions
            }
            selectedBrands={selectedBrands}
          />
        )}
      </Grid>
      {loading &&
        Array(1)
          .fill(null)
          .map((_, index) => (
            <Grid container spacing={2}>
              <Grid key={index}  size={{ xs: 12, md: 4 }} >
                <SkeletonCard key={index} />
              </Grid>
              <Grid key={index}  size={{ xs: 12, md: 8 }} >
                <SkeletonCard key={index} />
              </Grid>
            </Grid>
          ))}
    </Box>
  );
}
